import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster } from "./ui/sonner";
import Header from "./Header";

const LOGO_SMALL = "/assets/logo_small.png";

// Video paths from public/assets
const VIDEOS = {
  francesco: "/assets/francesco.mp4",
  lucia: "/assets/lucia.mp4",
  marco: "/assets/marco.mp4",
  sara: "/assets/sara.mp4",
  edoardo: "/assets/edoardo.mp4"
};

// Community Content Translations
const communityContent = {
  it: {
    nav: {
      signIn: "Accedi",
      register: "Registrati",
      menu: {
        rules: "Regolamento",
        community: "Community",
        topPlayers: "Top Players",
        playerValues: "Prezzi Giocatori",
        download: "Scarica l'App",
        contact: "Contatti"
      }
    },
    footer: {
      rights: "Tutti i diritti riservati.",
      contact: "Domande o feedback?"
    },
    pageTitle: "LA COMMUNITY FANTEEX",
    intro: "Cinque persone, cinque modi diversi di vivere il fantasy.",
    testimonials: [
      {
        name: "Francesco",
        video: VIDEOS.francesco,
        quote: "Mi piace Fanteex perché ogni giornata posso scegliere davvero i giocatori che voglio."
      },
      {
        name: "Lucia",
        video: VIDEOS.lucia,
        quote: "Adoro Fanteex: anche se non gioco per un po', non esco mai dalle classifiche e posso rimettermi in competizione quando voglio."
      },
      {
        name: "Marco",
        video: VIDEOS.marco,
        quote: "Finalmente non devo più scegliere giocatori contro la mia squadra del cuore."
      },
      {
        name: "Sara",
        video: VIDEOS.sara,
        quote: "Ogni giornata tante nuove sfide: Fanteex è troppo coinvolgente, non riesco a smettere di giocare."
      },
      {
        name: "Edoardo",
        video: VIDEOS.edoardo,
        quote: "Ci voleva Fanteex. Adoro fare le sfide leghe con i miei amici e ogni settimana dimostrare che sono troppo più forte!"
      }
    ]
  },
  en: {
    nav: {
      signIn: "Sign In",
      register: "Register",
      menu: {
        rules: "Game Rules",
        community: "Community",
        topPlayers: "Top Players",
        playerValues: "Player Values",
        download: "Download App",
        contact: "Get in Touch"
      }
    },
    footer: {
      rights: "All rights reserved.",
      contact: "Questions or feedback?"
    },
    pageTitle: "THE FANTEEX COMMUNITY",
    intro: "Five people, five different ways to experience fantasy football.",
    testimonials: [
      {
        name: "Francesco",
        video: VIDEOS.francesco,
        quote: "I love Fanteex because every matchday I can truly choose the players I want."
      },
      {
        name: "Lucia",
        video: VIDEOS.lucia,
        quote: "I love Fanteex: even if I don't play for a while, I never drop out of the rankings and can get back into competition whenever I want."
      },
      {
        name: "Marco",
        video: VIDEOS.marco,
        quote: "Finally I don't have to pick players against my favorite team anymore."
      },
      {
        name: "Sara",
        video: VIDEOS.sara,
        quote: "Every matchday brings new challenges: Fanteex is so engaging, I can't stop playing."
      },
      {
        name: "Edoardo",
        video: VIDEOS.edoardo,
        quote: "Fanteex was needed. I love doing league challenges with my friends and proving every week that I'm way better!"
      }
    ]
  }
};

// Tech Background Component
const TechBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
    <div className="absolute top-[-20%] left-[-20%] w-[80vw] h-[80vw] bg-[#1a0b2e] rounded-full blur-[100px] opacity-80" />
    <div className="absolute bottom-[-20%] right-[-20%] w-[80vw] h-[80vw] bg-[#0f0518] rounded-full blur-[100px] opacity-90" />
    <div className="absolute inset-0 bg-[linear-gradient(rgba(93,253,203,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(93,253,203,0.03)_1px,transparent_1px)] bg-[size:100px_100px]" />
  </div>
);

// Testimonial Card Component with Intersection Observer for play on scroll
// Alternating layout: even index = text left, video right | odd index = video left, text right
const TestimonialCard = ({ testimonial, index }) => {
  const [videoError, setVideoError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const videoRef = useRef(null);
  const cardRef = useRef(null);
  const isReversed = index % 2 !== 0; // Odd indexes have reversed layout

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.3,
        rootMargin: "-5% 0px"
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      if (isVisible) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }
  }, [isVisible]);

  // Text/Quote Section
  const TextSection = () => (
    <div className="flex flex-col justify-center p-6 md:p-8 lg:p-10">
      <h3 className="text-[#5DFDCB] font-display font-black uppercase text-2xl md:text-3xl lg:text-4xl tracking-wider mb-4">
        {testimonial.name}
      </h3>
      <p className="text-white/90 font-sans text-base md:text-lg lg:text-xl leading-relaxed italic">
        "{testimonial.quote}"
      </p>
    </div>
  );

  // Video Section
  const VideoSection = () => (
    <div className="relative aspect-video rounded-xl overflow-hidden bg-black/60">
      {!videoError ? (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          autoPlay
          preload="metadata"
          onError={() => setVideoError(true)}
          aria-label={`Testimonianza di ${testimonial.name}`}
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={testimonial.video} type="video/mp4" />
        </video>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a0b2e] to-[#0f0518] flex items-center justify-center">
          <span className="text-[#5DFDCB] text-8xl font-display font-black opacity-20">
            {testimonial.name.charAt(0)}
          </span>
        </div>
      )}

      {/* Playing indicator */}
      {isVisible && !videoError && (
        <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/50 px-2 py-1 rounded-full">
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-white/70 text-xs font-sans uppercase">Live</span>
        </div>
      )}
    </div>
  );

  return (
    <motion.div
      ref={cardRef}
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.1 * index }}
      className="community-card group"
    >
      <div className="bg-black/40 backdrop-blur-xl border border-[#5DFDCB]/20 rounded-2xl overflow-hidden hover:border-[#5DFDCB]/40 transition-all duration-300">
        {/* Mobile: Always video on top, text below */}
        <div className="md:hidden">
          <VideoSection />
          <TextSection />
        </div>

        {/* Desktop: Alternating layout */}
        <div className={`hidden md:grid md:grid-cols-2 ${isReversed ? '' : ''}`}>
          {isReversed ? (
            <>
              <VideoSection />
              <TextSection />
            </>
          ) : (
            <>
              <TextSection />
              <VideoSection />
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const CommunityPage = () => {
  const [lang, setLang] = useState('it');
  const t = communityContent[lang];

  const toggleLanguage = () => {
    setLang(prev => prev === 'it' ? 'en' : 'it');
  };

  return (
    <div className="relative min-h-screen bg-fanteex-dark overflow-x-hidden flex flex-col font-sans selection:bg-[#5DFDCB] selection:text-fanteex-dark">
      {/* Background */}
      <div className="fixed inset-0 z-0 pointer-events-none bg-[#0f0518]">
        <div className="absolute inset-0 opacity-15 mix-blend-overlay" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/noise.svg)` }}></div>
      </div>

      <TechBackground />

      {/* Navigation */}
      <Header t={t} lang={lang} toggleLanguage={toggleLanguage} />

      {/* Page Content */}
      <main className="flex-1 pt-32 pb-20 px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Page Title */}
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-6xl lg:text-7xl font-display font-black text-white uppercase mb-4 text-center"
          >
            {t.pageTitle}
          </motion.h1>

          {/* Intro Text */}
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-center text-white/70 font-sans text-lg md:text-xl mb-12 max-w-2xl mx-auto"
          >
            {t.intro}
          </motion.p>

          {/* Testimonials Grid */}
          <div className="community-grid">
            {t.testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={testimonial.name}
                testimonial={testimonial}
                index={index}
              />
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full py-12 border-t border-white/5 bg-black text-center relative z-20">
        <Link to="/">
          <img src={LOGO_SMALL} alt="Fanteex" className="h-8 w-auto mx-auto mb-4 opacity-50 grayscale hover:grayscale-0 transition-all" />
        </Link>
        <p className="text-fanteex-textMuted/50 text-xs font-sans uppercase tracking-widest mb-2">
          &copy; 2026 Fanteex. {t.footer.rights}
        </p>
        <p className="text-fanteex-textMuted/30 text-xs font-sans">
          {t.footer.contact}{" "}
          <a href="mailto:info@fanteex.com" className="text-[#5DFDCB] hover:underline">
            info@fanteex.com
          </a>
        </p>
      </footer>

      <Toaster position="bottom-center" theme="dark" />
    </div>
  );
};

export default CommunityPage;
